<template>
  <div class="login-scene">
    <div class="login-panel">
      <div class="login-card">
        <!-- HEADER -->
        <div class="card-top">
          <div class="top-title-group">
            <span class="top-label">National University - EMS</span>
            <span class="top-title">Student Login</span>
          </div>
          <!-- BADGE: sits in header, bottom half overlaps the stripe via position -->
          <div class="logo-badge" aria-label="National University Logo">
            <v-img
              :src="require('@/assets/nu_logos/nu_logo_r.png')"
              contain
              width="82"
              height="82"
              alt="NU Logo"
            />
          </div>
        </div>

        <!-- ACCENT STRIPE -->
        <div class="accent-stripe" />

        <!-- BADGE SPACER: reserves room for badge overflow below stripe -->
        <div class="badge-spacer" />

        <!-- BODY -->
        <div class="card-body">
          <!-- STEP INDICATOR -->
          <!-- <div class="step-row" aria-label="Login steps">
            <div class="step-dot active">1</div>
            <span class="step-lbl active">Identify</span>
            <div class="step-line" />
            <div class="step-dot">2</div>
            <span class="step-lbl">Verify</span>
            <div class="step-line" />
            <div class="step-dot">3</div>
            <span class="step-lbl">Access</span>
          </div> -->

          <!-- ERROR -->
          <!-- <transition name="fade-slide">
            <div v-if="errorMessage" class="error-bar" role="alert">
              <v-icon small style="color:#C62828;">error_outline</v-icon>
              {{ errorMessage }}
              <button
                class="error-close"
                @click="errorMessage = ''"
                aria-label="Dismiss"
              >
                <v-icon small style="color:#C62828;">close</v-icon>
              </button>
            </div>
          </transition> -->

          <!-- REGISTRATION -->
          <div class="field-block">
            <label class="field-label" for="reg-num">
              <v-icon small class="lbl-icon">badge</v-icon>
              Registration Number
            </label>
            <input
              id="reg-num"
              v-model="user.username"
              class="field-input"
              :class="{ 'field-input--err': errors.username }"
              type="text"
              placeholder="e.g. 20000000005"
              autocomplete="username"
              spellcheck="false"
              @keyup.enter="checkUser"
            />
            <p v-if="errors.username" class="field-err-msg">
              <v-icon x-small style="color:#E53935;">warning</v-icon>
              {{ errors.username }}
            </p>
          </div>

          <!-- CAPTCHA LABEL -->
          <div class="sec-label">
            <v-icon small class="lbl-icon">verified_user</v-icon>
            Security Verification
          </div>

          <!-- CAPTCHA CARD -->
          <div class="cap-card">
            <div class="cap-img-box">
              <img
                v-if="captcha.img"
                :src="captcha.img"
                alt="Math CAPTCHA"
                class="cap-img"
              />
              <div v-else class="cap-loading">
                <v-progress-circular
                  indeterminate
                  color="#3F51B5"
                  size="20"
                  width="2"
                />
              </div>
            </div>
            <span class="captcha-equals" aria-hidden="true">=</span>
            <div class="cap-right">
              <!-- <p class="cap-sublabel">Your answer</p> -->

              <div class="cap-input-row">
                <input
                  v-model="user.captcha"
                  class="cap-input"
                  :class="{ 'field-input--err': errors.captcha }"
                  type="number"
                  placeholder="Your answer"
                  inputmode="numeric"
                  autocomplete="off"
                  @keyup.enter="checkUser"
                />
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <button
                      type="button"
                      class="ref-btn"
                      :class="{ spinning: refreshing }"
                      v-on="on"
                      aria-label="New CAPTCHA problem"
                      @click="loadCaptcha"
                    >
                      <v-icon style="color:#fff;font-size:16px;"
                        >refresh</v-icon
                      >
                    </button>
                  </template>
                  <span>New problem</span>
                </v-tooltip>
              </div>
              <!-- <p v-if="errors.captcha" class="field-err-msg">
                <v-icon x-small style="color:#E53935;">warning</v-icon>
                {{ errors.captcha }}
              </p> -->
              <!-- <p class="cap-note">
                <v-icon x-small style="color:#9FA8DA;">info</v-icon>
                Enter the numeric result
              </p> -->
            </div>
          </div>

          <div
            style="margin-left: 47%; width:100%; text-align:end; margin-top:6px;"
          >
            <p v-if="errors.captcha" class="field-err-msg" style="margin:0;">
              <v-icon x-small style="color:#E53935;">warning</v-icon>
              {{ errors.captcha }}
            </p>
          </div>

          <!-- HINT -->
          <div class="hint-box" role="note">
            <v-icon small class="hint-icon">lightbulb_outline</v-icon>
            <span class="hint-text">
              <strong>Hint:</strong> Solve the math problem to prove you are
              human and complete login.
            </span>
          </div>
        </div>

        <!-- DIVIDER -->
        <div class="card-divider" />

        <!-- SUBMIT -->
        <div class="card-actions">
          <button
            class="submit-btn"
            :class="{ 'submit-btn--loading': loading }"
            :disabled="loading"
            @click="checkUser"
          >
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="white"
              size="16"
              width="2"
              class="mr-2"
            />
            <v-icon v-else style="color:#fff;font-size:17px;margin-right:7px;"
              >login</v-icon
            >
            <!-- {{ loading ? "Signing in…" : "Sign In to Portal" }} -->
            LOGIN
          </button>
        </div>
        <v-snackbar
          v-model="snackbar"
          top
          right
          :timeout="6000"
          color="red darken-2"
          content-class="custom-snackbar"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-3" color="white">
              error_outline
            </v-icon>

            <div class="snackbar-message">
              <span v-if="errorLogMsg">{{ errorLogMsg.message }}</span> <br>
              <!-- <span v-if="errorMessage">{{ errorMessage }} </span> -->
            </div>
          </div>

          <v-btn slot="action" icon dark @click="snackbar = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-snackbar>

        <!-- FOOTER -->
        <!-- <div class="card-footer">
          <div class="foot-item">
            <v-icon x-small class="foot-icon">lock</v-icon>
            Secure connection
          </div>
          <div class="dot-sep" aria-hidden="true" />
          <div class="foot-item">
            <v-icon x-small class="foot-icon">verified</v-icon>
            NU Bangladesh
          </div>
          <div class="dot-sep" aria-hidden="true" />
          <div class="foot-item">
            <v-icon x-small class="foot-icon">school</v-icon>
            Students only
          </div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StudentLogin",

  data() {
    return {
      snackbar: false,
      loading: false,
      refreshing: false,
      errorMessage: "",
      errors: { username: "", captcha: "" },
      user: {
        username: "",
        password: "123456",
        captcha: "",
        key: "",
      },
    };
  },

  mounted() {
    localStorage.clear();
    this.loadCaptcha();
  },

  computed: {
    captcha() {
      return this.$store.getters.getCaptcha;
    },
    errorLogMsg() {
      return this.$store.getters.getErrorLog || {};
    },
  },
  watch: {
    errorLogMsg(val) {
      if (val && val.message) {
        this.snackbar = true;
      }
    },
  },

  methods: {
    loadCaptcha() {
      this.refreshing = true;
      this.$store
        .dispatch("loadCaptcha")
        .then((data) => {
          this.user.key = data.key;
          this.user.captcha = "";
          this.errors.captcha = "";
        })
        .finally(() => {
          setTimeout(() => {
            this.refreshing = false;
          }, 400);
        });
    },

    validate() {
      this.errors = { username: "", captcha: "" };
      let ok = true;
      if (!this.user.username.trim()) {
        this.errors.username = "Registration number is required.";
        ok = false;
      }
      if (!String(this.user.captcha).trim()) {
        this.errors.captcha = "Security Verification is required.";
        ok = false;
      }
      return ok;
    },

    // checkUser() {
    //   if (!this.validate()) return;
    //   this.errorMessage = "";
    //   this.loading = true;
    //   this.$store
    //     .dispatch("authenticateStudent", this.user)
    //     .then(() => {
    //       this.$router.push("/student/dashboard");
    //     })
    //     .catch(() => {
    //       this.errorMessage =
    //         "Incorrect registration number or CAPTCHA answer. Please try again.";
    //       this.loadCaptcha();
    //     })
    //     .finally(() => {
    //       this.loading = false;
    //     });
    // },
    checkUser(event) {
      if (event && event.preventDefault) event.preventDefault();
      if (!this.validate()) return;
      this.errorMessage = "";
      this.loading = true;
      this.$store
        .dispatch("authenticateStudent", this.user)
        .then(() => {
          this.$router.push("/student/dashboard");
        })
        .catch(() => {
          this.errorMessage =
            "Incorrect Registration Number or CAPTCHA answer.";
          // Only reload captcha, don't do anything else
          this.loadCaptcha();
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* ── SCENE ── */
.login-scene {
  min-height: 100vh;
  background: #eceef8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}

.login-panel {
  width: 100%;
  max-width: 400px;
}

/* ── CARD ──
   overflow: visible so badge can poke out below the stripe.
   The card border + rounding is handled by card-top (top corners)
   and the card itself (bottom corners). */
.login-card {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  border: 1px solid #dcdff0;
  position: relative;
  box-shadow: rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
    rgba(0, 0, 0, 0.3) 0px 30px 60px -30px,
    rgba(10, 37, 64, 0.35) 0px -2px 6px 0px inset;
}

/* ── HEADER ── */
.card-top {
  background: #3f51b5;
  padding: 5px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 15px 15px 0 0;
  position: relative;
  /* allow badge to overflow downward */
  overflow: visible;
}

.top-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: -10px;
}

.top-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.top-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

/* ── LOGO BADGE ──
   Anchored inside .card-top flex, then pushed down so the
   bottom half crosses the stripe into the white body. */
.logo-badge {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: #ffffff;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  /* shift badge down by half its height so it straddles the stripe */
  position: relative;
  bottom: -28px;
  z-index: 10;
}

/* ── STRIPE ── */
.accent-stripe {
  height: 3px;
  background: linear-gradient(90deg, #5c6bc0, #9fa8da);
}

/* ── BADGE SPACER ──
   Transparent row that reserves vertical space equal to
   the half of the badge that overflows below the stripe. */
.badge-spacer {
  height: 38px; /* (badge 64px / 2) + a little breathing room */
}

/* ── BODY ── */
.card-body {
  padding: 4px 20px 16px;
}

/* ── STEPS ── */
.step-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
}

.step-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e8eaf6;
  border: 1.5px solid #c5cae9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #9fa8da;
  flex-shrink: 0;
  transition: all 0.2s;
}

.step-dot.active {
  background: #3f51b5;
  border-color: #3f51b5;
  color: #ffffff;
}

.step-lbl {
  font-size: 10px;
  font-weight: 600;
  color: #b0bad0;
  white-space: nowrap;
}

.step-lbl.active {
  color: #3f51b5;
}

.step-line {
  flex: 1;
  height: 1px;
  background: #e0e4f0;
}

/* ── ERROR ── */
.error-bar {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12.5px;
  color: #c62828;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.error-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* ── FIELDS ── */
.field-block {
  margin-bottom: 14px;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #7986cb;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  margin-bottom: 6px;
}

.lbl-icon {
  font-size: 13px !important;
  color: #7986cb !important;
}

.field-input {
  width: 100%;
  background: #f4f5fb;
  border: 1.5px solid #e0e4f0;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 14px;
  color: #1a1f3c;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
  -webkit-appearance: none;
}

.field-input:focus {
  border-color: #3f51b5;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

.field-input::placeholder {
  color: #b0bad0;
}
.field-input--err {
  border-color: #e53935 !important;
}

.field-err-msg {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #e53935;
  margin-top: 4px;
}

/* ── HINT ── */
.hint-box {
  background: #e8eaf6;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 34px;
}

.hint-icon {
  color: #3f51b5 !important;
  font-size: 15px !important;
  flex-shrink: 0;
  margin-top: 1px;
}
.hint-text {
  font-size: 12px;
  color: #3949ab;
  line-height: 1.5;
}

/* ── SEC LABEL ── */
.sec-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #7986cb;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  margin-bottom: 10px;
  margin-top: 30px;
}

/* ── CAPTCHA ── */
.cap-card {
  /* background: #F4F5FB; */
  /* border: 1.5px solid #E0E4F0; */
  border-radius: 12px;
  /* padding: 12px; */
  display: flex;
  gap: 8px;
  align-items: center;
}

.cap-img-box {
  /* flex: 1.2;
  background: #ffffff;
  border: 1.5px solid #C5CAE9;
  border-radius: 8px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  */
}

.cap-img {
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
  border-radius: 8px;
}

.captcha-equals {
  font-size: 20px;
  font-weight: 500;
  color: #9e9e9e;
  flex-shrink: 0;
  user-select: none;
  line-height: 1;
  margin-top: 5px;
}

.cap-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 60px;
}

.cap-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cap-sublabel {
  font-size: 10px;
  font-weight: 600;
  color: #9fa8da;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 0px;
}

.cap-input-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.cap-input {
  flex: 1;
  width: 0;
  background: #ffffff;
  border: 1.5px solid #c5cae9;
  border-radius: 8px;
  padding: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #283593;
  text-align: center;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
  -webkit-appearance: none;
  height: 60px;
  /* margin-top: 8px; */
}

.cap-input:focus {
  border-color: #3f51b5;
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

.cap-input::placeholder {
  color: #c5cae9;
  font-weight: 400;
  font-size: 13px;
}

.ref-btn {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  background: #3f51b5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.ref-btn:hover {
  background: #3949ab;
}
.spinning .v-icon {
  animation: spin360 0.45s linear;
}
@keyframes spin360 {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.cap-note {
  font-size: 11px;
  color: #9fa8da;
  display: flex;
  align-items: center;
  gap: 3px;
}

/* ── DIVIDER + ACTIONS ── */
.card-divider {
  height: 1px;
  background: #f0f2f8;
}

.card-actions {
  padding: 16px 20px;
  margin-bottom: 16px;
}

.submit-btn {
  width: 100%;
  background: #3f51b5;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  letter-spacing: 0.2px;
  transition: background 0.15s, transform 0.1s;
}

.submit-btn:hover:not(:disabled) {
  background: #3949ab;
}
.submit-btn:active:not(:disabled) {
  transform: scale(0.99);
}
.submit-btn--loading,
.submit-btn:disabled {
  background: #7986cb;
  cursor: not-allowed;
}

/* ── FOOTER ── */
.card-footer {
  padding: 10px 20px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-top: 1px solid #f0f2f8;
  flex-wrap: wrap;
}

.foot-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: #b0bad0;
}

.foot-icon {
  color: #b0bad0 !important;
  font-size: 12px !important;
}
.dot-sep {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #d0d4e8;
}

/* ── RESPONSIVE ── */
@media (max-width: 360px) {
  .card-body {
    padding: 4px 16px 12px;
  }
  .card-actions {
    padding: 14px 16px;
  }
  .card-footer {
    padding: 8px 16px 12px;
    gap: 8px;
  }
  .top-title {
    font-size: 16px;
  }
  .logo-badge {
    width: 82px;
    height: 82px;
    bottom: -22px;
  }
  .badge-spacer {
    height: 28px;
  }
  .step-lbl {
    display: none;
  }
  .cap-card {
    flex-wrap: wrap;
  }
  .cap-img-box {
    flex: 1 1 100%;
  }
  .captcha-equals {
    display: none;
  }
  .cap-right {
    flex: 1 1 100%;
  }
}

@media (max-width: 480px) {
  .login-panel {
    max-width: 100%;
  }
  .step-lbl {
    font-size: 9px;
  }
}

@media (max-width: 599px) and (orientation: landscape) {
  .login-scene {
    align-items: flex-start;
    padding-top: 12px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .login-panel {
    max-width: 390px;
  }
}

@media (min-width: 1440px) {
  .login-panel {
    max-width: 420px;
  }
}

@media (hover: none) and (pointer: coarse) {
  .field-input {
    font-size: 16px;
  }
  .cap-input {
    font-size: 16px;
  }
  .submit-btn {
    min-height: 50px;
  }
  .ref-btn {
    width: 44px;
    height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    transition: none !important;
    animation: none !important;
  }
}
</style>
